import React, { Fragment, memo, useLayoutEffect, useRef, useState } from 'react';
import InfiniteScroll, { Direction } from '@dealroadshow/uikit-infinite-scroll';
import Spinner from '@dealroadshow/uikit-loader/Spinner';
import { Textarea } from '@dealroadshow/uikit-textarea';
import Flex from '@creditflowresearch/uilib/Flex';
import IconArrowUpStroke from '@creditflowresearch/uilib/Icon/IconArrowUpStroke';
import IconStop from '@creditflowresearch/uilib/Icon/IconStop';
import { useAskAiChatContext } from '@/creditflowresearch/application/AskAiChat/context/AskAiChatContext';
import { useUserTimeZoneContext } from '@/creditflowresearch/application/User/UserTimeZone/UserTimeZoneContext';
import { MAX_QUESTION_LENGTH } from '@/creditflowresearch/application/AskAiChat/constants';
import { getTurnEndStatuses } from '@/creditflowresearch/application/AskAiChat/helpers/chatMessages';
import Message from './Message';
import ZeroCase from './ZeroCase';
import PromptVersionSelect from './PromptVersionSelect';
import ManagePromptsLink from './ManagePromptsLink';
import { getRequestContent, groupMessagesByDate } from './utils';
import styles from './askAiChat.module.scss';

const NEAR_BOTTOM_PX = 40;

const AskAiChatView = () => {
  const {
    messages,
    isGenerating,
    isInitializing,
    isFetchingMessages,
    hasMoreMessages,
    connectionError,
    send,
    cancel,
    retry,
    loadOlderMessages,
  } = useAskAiChatContext();

  const { timeZone } = useUserTimeZoneContext();
  const [question, setQuestion] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isPagingRef = useRef(false);
  const isNearBottomRef = useRef(true);

  const isSendDisabled = !question.trim().length || isGenerating;

  useLayoutEffect(() => {
    if (isInitializing) isNearBottomRef.current = true;
  }, [isInitializing]);

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (isPagingRef.current) {
      isPagingRef.current = false;
      return;
    }

    if (isNearBottomRef.current) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    isNearBottomRef.current = container.scrollHeight - container.scrollTop - container.clientHeight < NEAR_BOTTOM_PX;
  };

  const sendQuestion = (text: string) => {
    isNearBottomRef.current = true;
    send(text);
  };

  const handleRetry = (messageId: string) => {
    isNearBottomRef.current = true;
    retry(messageId);
  };

  const handleSend = () => {
    if (isSendDisabled) return;

    sendQuestion(question);
    setQuestion('');
  };

  const handleEdit = (messageId: string) => {
    const request = getRequestContent(messages, messageId);
    if (request === null) return;

    setQuestion(request.slice(0, MAX_QUESTION_LENGTH));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleFetchOlder = () => {
    isPagingRef.current = true;
    loadOlderMessages();
  };

  const groups = groupMessagesByDate(messages, timeZone);
  const turnStatuses = getTurnEndStatuses(messages);

  const content = messages.length ? groups.map((group) => (
    <Fragment key={ group.label }>
      <div className={ styles.dateLabel } data-test="askAiChatDate">{ group.label }</div>
      { group.messages.map((message) => (
        <Message
          key={ message.id }
          message={ message }
          turnStatus={ turnStatuses.get(message.id) }
          onRetry={ handleRetry }
          onEdit={ handleEdit }
        />
      )) }
    </Fragment>
  )) : <ZeroCase onPrompt={ sendQuestion } />;

  return (
    <div className={ styles.chat } data-test="askAiChat">
      <InfiniteScroll
        fetchCallback={ handleFetchOlder }
        isFetching={ isFetchingMessages }
        scrollContainerRef={ scrollContainerRef.current }
        isAllItemsFetched={ !hasMoreMessages }
        direction={ Direction.UP }
      >
        <div className={ styles.scroller } ref={ scrollContainerRef } onScroll={ handleScroll }>
          { isInitializing ? <Spinner centered /> : content }
        </div>
      </InfiniteScroll>

      { connectionError && (
        <div className={ styles.connectionError } data-test="askAiChatConnectionError">
          { connectionError }
        </div>
      ) }

      <div className={ styles.composer }>
        <div className={ styles.composerField }>
          <Textarea
            // @ts-ignore uikit Textarea types the input prop as a final-form field
            placeholder="Ask anything..."
            formFieldClassName={ styles.textareaField }
            textareaClassName={ styles.textarea }
            dataTest="askAiChatTextarea"
            input={ {
              value: question,
              onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => setQuestion(event.target.value),
              onKeyDown: handleKeyDown,
            } }
            rows={ 4 }
            maxLength={ MAX_QUESTION_LENGTH }
          />
          <button
            type="button"
            className={ styles.sendButton }
            onClick={ isGenerating ? cancel : handleSend }
            disabled={ isGenerating ? false : isSendDisabled }
            aria-label={ isGenerating ? 'Stop generating' : 'Send' }
            data-test={ isGenerating ? 'askAiChatStopButton' : 'askAiChatSendButton' }
          >
            { isGenerating
              ? <IconStop className={ styles.sendIcon } />
              : <IconArrowUpStroke className={ styles.sendIcon } /> }
          </button>
        </div>
        <Flex
          align="center"
          alignSelf="flex-start"
          wrap="wrap"
          gap={ 12 }
          className={ styles.promptControls }
          dataTest="askAiPromptControls"
        >
          <PromptVersionSelect />
          <ManagePromptsLink />
        </Flex>
        <p className={ styles.disclaimer }>
          CFR AI can make mistakes. Please double-check responses.
        </p>
      </div>
    </div>
  );
};

export default memo(AskAiChatView);
